/**
 * Which build of the widget is on the page.
 *
 * 🔴 THIS EXISTS BECAUSE "IS THIS THE NEW ONE?" HAD NO ANSWER. Testing against
 *    a customer site meant reading the UI and guessing: a missing elapsed time
 *    on the tool summary looks exactly the same whether the feature is broken
 *    or the browser is holding a bundle from yesterday. That guess has already
 *    cost an evening once at the API layer, where a stale systemd unit served
 *    old code for 22 hours while the new code sat in dist (docs/13, 3bu).
 *
 *    A build stamp turns that question into a glance. It is not for the
 *    customer's visitors, who will never look at it. It is for whoever is
 *    trying to work out whether they are testing what they think they are.
 *
 * 🔴 TWO VALUES, AND NEITHER ONE REPLACES THE OTHER.
 *
 *    VERSION is the release number a person can say out loud and compare.
 *    BUILD is the commit, which is what actually proves which bytes are on the
 *    page. The version is only as truthful as somebody remembering to bump it,
 *    so on its own it can be confidently wrong: ship twice without bumping and
 *    both builds claim to be the same thing. The commit cannot be wrong and
 *    cannot be remembered, which is why it stays.
 *
 *    Together they answer different questions. "Which release is this?" and
 *    "am I looking at the bundle I just deployed?"
 *
 * 🔴 ONLY THE VERSION IS PRINTED. THE BUILD IS THE ELEMENT'S `title`.
 *
 *    Vishnu's call, and the reason is the audience. This footer renders on a
 *    customer's own site, under their users' eyes, and "2026-09-11 a9c1889"
 *    reads to them as debug output somebody forgot to take out. "Beta · v0.0.1"
 *    reads as a product.
 *
 *    The commit is not deleted, because it is the half that cannot lie and the
 *    question it answers came up the same day it was added: a dashboard tab
 *    holding a ten-minute-old bundle looked exactly like a broken deploy.
 *    Hovering the line still gives it, and it is in the DOM for anyone reading
 *    a screenshot of the markup. What changed is who has to look at it.
 */

/** Both replaced at build time by vite `define`. Absent under vitest and `vite dev`. */
declare const __VERB_BUILD__: string | undefined;
declare const __VERB_VERSION__: string | undefined;

/**
 * Falls back rather than throwing or rendering "undefined".
 *
 * The guard is `typeof` on purpose: an undeclared global is a ReferenceError on
 * plain access, and these strings are read while the panel's markup is being
 * built, so a throw here would take the whole widget down over a label.
 */
export const BUILD: string =
  typeof __VERB_BUILD__ === "string" && __VERB_BUILD__ ? __VERB_BUILD__ : "dev";

/**
 * From the widget's own package.json at build time, never typed out here.
 *
 * 🔴 ONE PLACE TO BUMP. A number written into this file as well as into
 *    package.json is a number that will disagree with itself the first time
 *    somebody bumps one of them, and the footer is the half nobody checks.
 */
export const VERSION: string =
  typeof __VERB_VERSION__ === "string" && __VERB_VERSION__ ? __VERB_VERSION__ : "0.0.0";
